import { TFunction } from "i18next";
import { useState } from "react";
import TrainingModule from "../../../models/trainingModule";
import { DeliveryMethod } from "../../../types/training";
import { DOWNLOAD_ALL_ASSETS_VALUE } from "../../../utils/Training.utils";
import { Button } from "../../ui/Button/Button";
import { Select, SelectItem } from "../../ui/Select/Select";
import { Tooltip } from "../../ui/Tooltip/Tooltip";
import { Typography } from "../../ui/Typography/Typography";
import { IconTypes } from "../../ui/icons";
import { Icon } from "../../ui/icons/Icon";
import "./TrainingModuleOverviewFooter.scss";

interface TrainingModuleOverviewFooterProps {
  assetDownloadError: boolean;
  canAccess: boolean;
  deliveryMethod?: DeliveryMethod;
  downloadError: boolean;
  dropdownItems: SelectItem[] | undefined;
  handleAssetSelect: (event: any) => void;
  refetchTrainingModule: () => void;
  t: TFunction<"trainingPage", undefined>;
  trainingModule: TrainingModule;
}

export const TrainingModuleOverviewFooter = ({
  assetDownloadError,
  canAccess,
  deliveryMethod,
  downloadError,
  dropdownItems,
  handleAssetSelect,
  refetchTrainingModule,
  t,
  trainingModule,
}: TrainingModuleOverviewFooterProps) => {
  const [isCopied, setIsCopied] = useState(false);
  const getIconTypeAndLabel = () => {
    if (deliveryMethod === DeliveryMethod.Link) {
      return {
        icon: isCopied ? IconTypes.Check : IconTypes.Copy,
        label: isCopied
          ? t("modules.delivery.copyAllSuccess")
          : t("modules.delivery.copyAll"),
      };
    }
    return {
      icon: IconTypes.Download,
      label: t("modules.delivery.downloadAll"),
    };
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(trainingModule.getAllTouchpointLinks());
    setIsCopied(true);
  };

  const handleDownloadOrCopy = () => {
    if (deliveryMethod === DeliveryMethod.Link) {
      handleCopy();
    } else {
      refetchTrainingModule();
    }
  };

  const { icon, label } = getIconTypeAndLabel();
  return (
    <div className="TrainingModuleOverviewFooter">
      {canAccess && (
        <div className="TrainingModuleOverviewFooter__assets">
          <div className="TrainingModuleOverviewFooter__assets__dropdown">
            <Tooltip
              title="Supporting material"
              cardContent={
                <div>
                  <Typography desktop="body1" mobile="body2">
                    {t("modules.assets.infoTitle")}
                  </Typography>
                  <Typography desktop="body1" color="secondary">
                    {t("modules.assets.infoText")}
                  </Typography>
                </div>
              }
              arrow
              placement="left"
            >
              <span className="TrainingModuleOverviewFooter__icon">
                <Icon type={IconTypes.Help} size={20} />
              </span>
            </Tooltip>
            <Select
              className="TrainingModuleOverviewFooter__assets__select"
              icon={IconTypes.Download}
              items={dropdownItems}
              itemClassName={(value) =>
                value === DOWNLOAD_ALL_ASSETS_VALUE
                  ? "TrainingModuleOverviewFooter__assets__select__item Select__special"
                  : "TrainingModuleOverviewFooter__assets__select__item "
              }
              label={t("modules.assets.downloadAssets")}
              onChange={handleAssetSelect}
              value=""
            />
          </div>
          {!!assetDownloadError && (
            <Typography color="error" desktop="caption">
              {t("modules.delivery.downloadError")}
            </Typography>
          )}
        </div>
      )}
      <div className="TrainingModuleOverviewFooter__module">
        {!trainingModule.canAccess && (
          <Typography
            color="error"
            desktop="caption"
            className="TrainingModuleOverviewFooter__module__disabled"
          >
            {t("modules.delivery.downloadDisabled")}
          </Typography>
        )}
        <div className="TrainingModuleOverviewFooter__module__download">
          <Button
            className="TrainingModuleOverviewFooter__module__download__button"
            onClick={handleDownloadOrCopy}
            variant="contained"
            color="info"
            small
            disabled={!canAccess}
          >
            <Icon type={icon} color="secondary" size={24} />
            {label}
          </Button>
          {deliveryMethod === DeliveryMethod.LMS && (
            <Tooltip
              title="Supporting material"
              cardContent={
                <div>
                  <Typography desktop="body1" mobile="body2">
                    {t("modules.delivery.infoTitle")}
                  </Typography>
                  <Typography desktop="body1" color="secondary">
                    {t("modules.delivery.infoText")}
                  </Typography>
                </div>
              }
              arrow
              placement="right"
            >
              <span className="TrainingModuleOverviewFooter__icon">
                <Icon type={IconTypes.Help} size={20} />
              </span>
            </Tooltip>
          )}
        </div>
        {!!downloadError && (
          <Typography color="error" desktop="caption">
            {t("modules.delivery.downloadError")}
          </Typography>
        )}
      </div>
    </div>
  );
};
